import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Phone, PhoneOff, Clock, CheckCircle2, X, Loader2, PhoneCall,
  AlertCircle, Mic, MicOff, Wifi, WifiOff, Link2, Minus, Maximize2
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSipPhone } from "@/components/sales/SipPhoneProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOMES = [
  { value: "erreicht", label: "Erreicht", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { value: "nicht_erreicht", label: "Nicht erreicht", color: "bg-red-500/20 text-red-400 border-red-500/30" },
  { value: "voicemail", label: "Mailbox", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  { value: "rueckruf", label: "Rückruf vereinbart", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "kein_interesse", label: "Kein Interesse", color: "bg-slate-500/20 text-slate-400 border-slate-500/30" },
];

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function CallTrackerDialog({ open, onClose, lead, onCallLogged }) {
  const sip = useSipPhone();
  const [phase, setPhase] = useState("ready"); // ready | active | logging
  const [elapsed, setElapsed] = useState(0);
  const [talkSeconds, setTalkSeconds] = useState(0);
  const [outcome, setOutcome] = useState("erreicht");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const tickRef = useRef(null);
  const startedAtRef = useRef(null);
  const answeredAtRef = useRef(null);

  const phone = lead?.contact_phone || "";

  // ── Minimize-Modus für Multi-Tasking während aktivem Anruf ──
  // User-Wunsch (2026-04-29): Vertriebspartner wollen während dem Telefonat
  // weiter im System arbeiten können. Aktiver Anruf darf nicht mehr durch
  // Outside-Click/Escape geschlossen werden — und kann zu einer kleinen
  // schwebenden Pill in der Ecke minimiert werden.
  const [minimized, setMinimized] = useState(false);
  // Pill-Position: persistiert in localStorage damit Vertriebler die einmal
  // an ihre Lieblings-Ecke ziehen und dort behalten können.
  const [pillPos, setPillPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("ah_call_pill_pos") || "null");
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") return saved;
    } catch (_e) {}
    return { x: window.innerWidth - 280, y: window.innerHeight - 80 };
  });
  const dragStateRef = useRef({ dragging: false, ox: 0, oy: 0, hasMoved: false });
  const onPillPointerDown = (ev) => {
    // Bei Click auf Restore-Icon nicht draggen — nur bei Drag-Bewegung
    dragStateRef.current = {
      dragging: true,
      ox: ev.clientX - pillPos.x,
      oy: ev.clientY - pillPos.y,
      hasMoved: false,
      startX: ev.clientX,
      startY: ev.clientY,
    };
    ev.currentTarget.setPointerCapture?.(ev.pointerId);
  };
  const onPillPointerMove = (ev) => {
    const s = dragStateRef.current;
    if (!s.dragging) return;
    const dx = ev.clientX - (s.startX || 0);
    const dy = ev.clientY - (s.startY || 0);
    if (!s.hasMoved && Math.hypot(dx, dy) < 5) return;
    s.hasMoved = true;
    const nx = Math.max(8, Math.min(window.innerWidth - 200, ev.clientX - s.ox));
    const ny = Math.max(8, Math.min(window.innerHeight - 60, ev.clientY - s.oy));
    setPillPos({ x: nx, y: ny });
  };
  const onPillPointerUp = (ev) => {
    const s = dragStateRef.current;
    if (s.hasMoved) {
      try { localStorage.setItem("ah_call_pill_pos", JSON.stringify(pillPos)); } catch (_e) {}
      ev.preventDefault();
      ev.stopPropagation();
    } else {
      // Klein-Klick → Modal restoren
      setMinimized(false);
    }
    dragStateRef.current = { dragging: false, ox: 0, oy: 0, hasMoved: false };
    ev.currentTarget.releasePointerCapture?.(ev.pointerId);
  };
  const callIsActive = phase === "active" || sip.callState === "calling" || sip.callState === "ringing" || sip.callState === "connected";
  // Outside-Click während aktivem Anruf abfangen (verriegeln statt schließen)
  const guardClose = (next) => {
    if (callIsActive && !next) {
      // statt schließen: minimieren
      setMinimized(true);
      return;
    }
    if (next === false || next === undefined) {
      onClose?.();
      setMinimized(false);
    }
  };
  // Wenn der Call endet, automatisch wieder ausklappen damit der User den
  // "Anruf nachverfolgen"-Logging-Step nicht verpasst.
  useEffect(() => {
    if (sip.callState === "ended" && minimized) setMinimized(false);
  }, [sip.callState, minimized]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase("ready");
      setElapsed(0);
      setTalkSeconds(0);
      setOutcome("erreicht");
      setNotes("");
      startedAtRef.current = null;
      answeredAtRef.current = null;
    } else {
      clearInterval(tickRef.current);
    }
  }, [open]);

  // Sync local timer from SIP state
  useEffect(() => {
    if (sip.callState === "calling" || sip.callState === "ringing") {
      startedAtRef.current = sip.callStartedAt || Date.now();
      setPhase("active");
    } else if (sip.callState === "connected") {
      answeredAtRef.current = sip.callConnectedAt || Date.now();
      setPhase("active");
    } else if (sip.callState === "ended") {
      setPhase("logging");
    }
  }, [sip.callState, sip.callStartedAt, sip.callConnectedAt]);

  // ── Pre-fill Outcome basierend auf tatsächlicher Termination-Ursache ──
  // SIP.js liefert via sip.lastCallResult, OB+WIE der Call beendet wurde.
  // Bei "rejected"/"no_answer"/"failed" hatte der User KEIN Gespräch — wir
  // pre-fillen das Outcome korrekt UND zeigen einen Status-Banner.
  // Bei "success" (Established → BYE) bleibt Default "erreicht".
  useEffect(() => {
    if (sip.callState !== "ended") return;
    const r = sip.lastCallResult;
    if (r === "rejected" || r === "no_answer") {
      setOutcome("nicht_erreicht");
    } else if (r === "failed") {
      // Failed = Verbindung kam gar nicht zustande — User soll wissen, dass
      // hier noch keine Aussage über den Lead getroffen werden kann.
      setOutcome("nicht_erreicht");
    } else if (r === "success") {
      setOutcome("erreicht");
    }
  }, [sip.callState, sip.lastCallResult]);

  // Local elapsed timer
  useEffect(() => {
    if (phase !== "active") {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      if (startedAtRef.current) setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      if (answeredAtRef.current) setTalkSeconds(Math.floor((Date.now() - answeredAtRef.current) / 1000));
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [phase]);

  const handleStartCall = async () => {
    if (!sip.registered) {
      toast.error("Telefon ist noch nicht verbunden");
      return;
    }
    await sip.call(phone);
  };

  const handleHangup = async () => {
    await sip.hangup();
    setPhase("logging");
  };

  const handleLogCall = async () => {
    setSaving(true);
    try {
      const seconds = talkSeconds || elapsed;
      const duration_min = Math.max(0, Math.round(seconds / 60));
      await axios.post(
        `${API}/leads/${lead.id || lead._id}/log-call`,
        { outcome, duration_min, notes, phone, call_id: null },
        { withCredentials: true }
      );
      toast.success("Anruf erfolgreich geloggt");
      onCallLogged?.();
      onClose();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const showTalk = sip.callState === "connected" || sip.callState === "ended";
  const phaseLabel = {
    calling: "Klingelt …",
    ringing: "Klingelt …",
    connected: "Verbunden",
    ended: "Beendet",
    idle: "",
  }[sip.callState] || "";

  return (
    <>
    {/* ── Minimized Floating Pill (während aktivem Anruf) ── */}
    {open && minimized && (
      <div
        role="button"
        tabIndex={0}
        onPointerDown={onPillPointerDown}
        onPointerMove={onPillPointerMove}
        onPointerUp={onPillPointerUp}
        data-testid="call-pill-restore"
        title="Ziehen zum Verschieben — Klick zum Öffnen"
        style={{ left: pillPos.x, top: pillPos.y, touchAction: "none" }}
        className={`fixed z-[60] flex items-center gap-3 px-4 py-3 rounded-full shadow-2xl border backdrop-blur-md transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,.5)] cursor-grab active:cursor-grabbing select-none
          ${sip.callState === "connected" ? "bg-green-600/95 text-white border-green-400/40" :
            sip.callState === "calling" || sip.callState === "ringing" ? "bg-amber-500/95 text-white border-amber-400/40 animate-pulse" :
            "bg-cyan-600/95 text-white border-cyan-400/40"}`}
        aria-label="Anruf-Panel öffnen (ziehbar)"
      >
        <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/20 pointer-events-none">
          {sip.callState === "connected" ? <PhoneCall size={16} /> : <Phone size={16} />}
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse"></span>
        </span>
        <span className="flex flex-col items-start leading-tight pointer-events-none">
          <span className="text-[11px] uppercase tracking-wider opacity-85 font-semibold">{phaseLabel || "Anruf"}</span>
          <span className="text-sm font-bold font-mono">{formatTime(elapsed)}</span>
        </span>
        <span className="text-xs font-medium pr-1 max-w-[180px] truncate hidden sm:inline pointer-events-none">{lead?.company_name}</span>
        <Maximize2 size={14} className="opacity-80 pointer-events-none" />
      </div>
    )}
    <Dialog
      open={open && !minimized}
      onOpenChange={(next) => { if (!next) guardClose(false); }}
    >
      <DialogContent
        className="max-w-sm p-0 overflow-hidden bg-[#0D1421] border border-white/10"
        onPointerDownOutside={(e) => { if (callIsActive) { e.preventDefault(); setMinimized(true); } }}
        onInteractOutside={(e) => { if (callIsActive) { e.preventDefault(); setMinimized(true); } }}
        onEscapeKeyDown={(e) => { if (callIsActive) { e.preventDefault(); setMinimized(true); } }}
      >
        <VisuallyHidden><DialogTitle>In-Browser-Anruf</DialogTitle></VisuallyHidden>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
              sip.callState === "connected" ? "bg-green-500/20 text-green-400 animate-pulse" :
              phase === "active"            ? "bg-amber-500/20 text-amber-400" :
              phase === "logging"           ? "bg-blue-500/20 text-blue-400" :
              "bg-cyan-500/20 text-cyan-400"
            }`}>
              {phase === "active" ? <PhoneCall size={14} /> : <Phone size={14} />}
            </div>
            <span className="font-semibold text-sm text-white">
              {phase === "ready"   && "Anruf starten"}
              {phase === "active"  && (phaseLabel || "Anruf läuft")}
              {phase === "logging" && "Anruf nachverfolgen"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {callIsActive && (
              <button
                type="button"
                onClick={() => setMinimized(true)}
                data-testid="call-minimize-btn"
                title="Minimieren — du kannst weiter im System arbeiten, der Anruf läuft weiter"
                className="text-white/50 hover:text-white p-1.5 rounded hover:bg-white/5 transition-colors"
                aria-label="Anruf-Panel minimieren"
              >
                <Minus size={16} />
              </button>
            )}
            <button onClick={onClose} className="text-white/40 hover:text-white p-1 hidden"><X size={16} /></button>
          </div>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Lead Card */}
          <div className="bg-white/3 border border-white/5 rounded-xl p-4">
            <p className="font-semibold text-white text-sm">{lead?.company_name}</p>
            <p className="text-sm text-cyan-400 mt-0.5 font-mono">{phone || "Keine Nummer"}</p>
            {lead?.contact_name && <p className="text-xs text-white/40 mt-1">{lead.contact_name}</p>}
          </div>

          {/* SIP Connection Bar (always visible) */}
          <div className="flex items-center gap-2 text-xs px-3 py-2 bg-white/3 border border-white/5 rounded-lg">
            {sip.registered ? (
              <><Wifi size={12} className="text-green-400"/><span className="text-green-400">Browser-Telefon verbunden</span></>
            ) : sip.registering ? (
              <><Loader2 size={12} className="animate-spin text-amber-400"/><span className="text-amber-400">Verbinde …</span></>
            ) : (
              <>
                <WifiOff size={12} className="text-red-400"/>
                <span className="text-red-400 flex-1">Telefon nicht verbunden</span>
                <button
                  onClick={sip.connect}
                  className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
                  data-testid="sip-connect-btn"
                >
                  <Link2 size={11}/> Verbinden
                </button>
              </>
            )}
          </div>
          {sip.error && (
            <div className="text-[10px] bg-red-500/10 border border-red-500/20 rounded p-2 text-red-300 font-mono">
              {sip.error}
            </div>
          )}

          {/* PHASE READY */}
          {phase === "ready" && (
            <div className="space-y-3">
              {!phone && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <AlertCircle size={14} className="text-amber-400"/>
                  <span className="text-xs text-amber-400">Keine Telefonnummer hinterlegt</span>
                </div>
              )}
              <Button
                onClick={handleStartCall}
                disabled={!phone || !sip.registered}
                data-testid="start-call-btn"
                className="w-full bg-green-500 hover:bg-green-400 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <Phone size={16}/> Anruf starten
              </Button>
              {!sip.registered && (
                <p className="text-xs text-amber-300 text-center">
                  Bitte zuerst auf „Verbinden" klicken, um über den Browser zu telefonieren.
                </p>
              )}
              <Button
                variant="outline"
                onClick={() => setPhase("logging")}
                className="w-full text-sm border-white/10 text-white/60 hover:text-white"
                data-testid="manual-log-btn"
              >
                Anruf manuell loggen
              </Button>
            </div>
          )}

          {/* PHASE ACTIVE */}
          {phase === "active" && (
            <div className="space-y-4">
              {/* Big timer */}
              <div className="text-center py-3">
                <div className={`text-5xl font-mono font-bold tracking-widest ${
                  sip.callState === "connected" ? "text-green-400" : "text-white/80"
                }`} data-testid="call-timer">
                  {formatTime(showTalk ? talkSeconds : elapsed)}
                </div>
                <p className="text-xs text-white/30 mt-1.5">
                  {showTalk ? "Gesprächsdauer" : `Klingelzeit ${formatTime(elapsed)}`}
                </p>
              </div>

              {/* Pulse */}
              <div className="flex justify-center gap-1.5">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full animate-bounce ${
                      sip.callState === "connected" ? "bg-green-400" : "bg-amber-400"
                    }`}
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>

              {/* Mute Button */}
              <button
                onClick={sip.toggleMute}
                disabled={sip.callState !== "connected"}
                data-testid="mute-btn"
                className={`w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm transition-colors disabled:opacity-40 ${
                  sip.muted ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                }`}
              >
                {sip.muted ? <><MicOff size={14}/> Stumm</> : <><Mic size={14}/> Mikrofon an</>}
              </button>

              <Button
                onClick={handleHangup}
                data-testid="hangup-btn"
                className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <PhoneOff size={16}/> Auflegen
              </Button>
            </div>
          )}

          {/* PHASE LOGGING */}
          {phase === "logging" && (
            <div className="space-y-4">
              {/* ── Termination-Cause-Banner ── */}
              {/* Macht transparent WARUM der Call endete — User weiß ob er
                  re-callen muss (failed) oder ein echtes Gespräch hatte. */}
              {sip.lastCallResult && sip.lastCallResult !== "success" && (
                <div
                  className={`flex items-start gap-2.5 p-3 rounded-lg border text-xs ${
                    sip.lastCallResult === "failed"
                      ? "bg-red-500/10 border-red-500/30 text-red-300"
                      : sip.lastCallResult === "rejected"
                      ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                      : "bg-blue-500/10 border-blue-500/30 text-blue-300"
                  }`}
                  data-testid="call-result-banner"
                >
                  <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                  <div className="flex-1">
                    <p className="font-semibold">
                      {sip.lastCallResult === "failed"   && "Verbindung nicht zustande gekommen"}
                      {sip.lastCallResult === "rejected" && "Gegenseite hat abgelehnt / besetzt"}
                      {sip.lastCallResult === "no_answer" && "Keine Antwort"}
                    </p>
                    <p className="opacity-80 mt-0.5 leading-relaxed">
                      {sip.lastCallResult === "failed" &&
                        "Der Anruf konnte nicht aufgebaut werden — meist Sipgate-Routing oder Netzwerk. Erneut versuchen oder Gegenseite manuell prüfen."}
                      {sip.lastCallResult === "rejected" &&
                        "Die Nummer ist erreichbar, hat aber besetzt geklingelt oder den Anruf aktiv abgelehnt."}
                      {sip.lastCallResult === "no_answer" &&
                        "Es klingelte, aber niemand ging ran. In ein paar Stunden erneut versuchen."}
                    </p>
                    <button
                      onClick={() => { setPhase("ready"); }}
                      data-testid="call-retry-btn"
                      className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/15 text-white text-[11px] font-semibold transition-colors"
                    >
                      <PhoneCall size={11}/> Erneut versuchen
                    </button>
                  </div>
                </div>
              )}

              {(talkSeconds > 0 || elapsed > 0) && (
                <div className="flex items-center gap-2 text-sm text-white/50">
                  <Clock size={14}/> Dauer:
                  <span className="font-mono text-white">{formatTime(talkSeconds || elapsed)}</span>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">Ergebnis</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {OUTCOMES.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setOutcome(o.value)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium text-left border transition-all ${
                        outcome === o.value ? o.color
                          : "bg-white/3 border-white/5 text-white/50 hover:border-white/15"
                      }`}
                    >{o.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">Notizen</p>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Was wurde besprochen?"
                  rows={3}
                  className="bg-white/5 border-white/8 text-white placeholder-white/25 resize-none text-sm"
                  data-testid="call-notes-input"
                />
              </div>

              <Button
                onClick={handleLogCall}
                disabled={saving}
                data-testid="save-call-btn"
                className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin"/> : <CheckCircle2 size={16}/>}
                {saving ? "Wird gespeichert…" : "Anruf speichern"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
